import { useDynamicConfig, useFeatureGate } from '@statsig/react-bindings';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { EventType } from '@/analytics/types';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import {
  EXTENDED_WARRANTY_2YEAR_SKU,
  EXTENDED_WARRANTY_3YEAR_SKU,
  MEMBERSHIP_1YEAR_SKU,
} from '@/app/[locale]/store/_consts';
import { useDiscountContext } from '@/app/[locale]/store/_contexts/DiscountProvider';
import type { UiDiscount } from '@/app/components/PageLayout/_hooks/discount/types';

import type { ModalButtonText, UseBundleStepReturn } from './types';

const useBundleStep = (): UseBundleStepReturn => {
  const {
    warrantySelection,
    membershipSelection,
    ringCount,
    setMembershipSelection,
    existingMembership,
    existingWarranty,
  } = useWizardStateContext();
  const { appliedDiscountCode } = useDiscountContext();
  const path = usePathname();

  const isWarrantyAvailable = useFeatureGate('extended_warranty').value;
  //This check for a discount code and whether the membership should be auto-selected
  const pdpDiscountArray = useDynamicConfig('discount_user_interface').value[
    'discountArray'
  ] as UiDiscount[];

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalButtonText, setModalButtonText] = useState<ModalButtonText>(
    'bundle_modal_skip_button',
  );

  const isMembershipSelect = pdpDiscountArray.find(
    (discountObj) =>
      discountObj.discountCode === appliedDiscountCode &&
      discountObj.selectMembership,
  );

  // existing warranty in cart
  let existingWarrantyValue = WarrantySelection.OneYear;
  if (existingWarranty?.skuCode === EXTENDED_WARRANTY_2YEAR_SKU) {
    existingWarrantyValue = WarrantySelection.TwoYear;
  } else if (existingWarranty?.skuCode === EXTENDED_WARRANTY_3YEAR_SKU) {
    existingWarrantyValue = WarrantySelection.ThreeYear;
  }

  // existing membership in cart
  const existingMembershipValue =
    existingMembership?.skuCode == MEMBERSHIP_1YEAR_SKU
      ? MembershipSelection.OneYear
      : MembershipSelection.OneMonth;

  const handleOpenModal = async (): Promise<void> => {
    setIsModalOpen(true);
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'add_to_cart',
      action: 'open_bundle_modal',
      path,
      location: 'wizard',
    });
  };

  // bundle modal cta change per selections
  useEffect(() => {
    if (
      warrantySelection === WarrantySelection.OneYear &&
      existingWarrantyValue === WarrantySelection.OneYear &&
      membershipSelection === MembershipSelection.OneMonth &&
      existingMembershipValue === MembershipSelection.OneMonth
    ) {
      setModalButtonText('bundle_modal_skip_button');
    } else if (
      ringCount > 0 &&
      (warrantySelection !== existingWarrantyValue ||
        membershipSelection !== existingMembershipValue)
    ) {
      setModalButtonText('bundle_cta_update_cart');
    } else {
      setModalButtonText('pdp_add_to_cart');
    }
  }, [
    warrantySelection,
    membershipSelection,
    existingWarrantyValue,
    existingMembershipValue,
    ringCount,
  ]);

  useEffect(() => {
    if (isMembershipSelect?.selectMembership) {
      setMembershipSelection(MembershipSelection.OneYear);
    }
  }, [isMembershipSelect, setMembershipSelection]);

  return {
    isWarrantyAvailable,
    isModalOpen,
    modalButtonText,
    onOpenModal: handleOpenModal,
    setIsModalOpen,
  };
};

export default useBundleStep;
